import { FC, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { INVERSIFY_TYPES } from '@edikk-edikk/ui';
import { IntlErrorCode } from '@formatjs/intl/src/error';
import { IntlProvider } from 'react-intl';
import { setMessages, useBaseState } from '@edikk-edikk/redux/slice-base';
import { MessagesType, LanguageControl } from '@edikk-edikk/shared';
import { useLocation } from 'react-router';
import { Provider } from 'inversify-react';
import { Container } from 'inversify';
import { Helmet } from 'react-helmet-async';
import loadable from '@loadable/component';
import { useAppDispatch } from '#components/redux/hooks';
import { App } from '#base/containers/App';
import { Feature } from '#components/feature';

type PropsType = {
  diContainer: Container;
};

const GlobalError = loadable(() => import('@edikk-edikk/ui/error/GlobalError'), {
  resolveComponent: ({ GlobalError: Component }) => Component,
});

const AppWrapper: FC<PropsType> = ({
  diContainer,
}) => {
  const { messages, featureDataList } = useBaseState();
  const { pathname } = useLocation();
  const languageControl = diContainer.get<LanguageControl>(INVERSIFY_TYPES.languageControl);
  const feature = diContainer.get<Feature>(INVERSIFY_TYPES.feature);
  const { language } = languageControl.getLanguageConfigByPathname(pathname);
  const dispatch = useAppDispatch();

  useEffect(() => {
    feature.setList(featureDataList);
  }, [feature, featureDataList]);

  useEffect(() => {
    if (messages) {
      return;
    }

    import(`web/translate/${language}.json`).then((loadedMessages: MessagesType): void => {
      dispatch(setMessages(loadedMessages));
    }).catch(() => null);
  }, [dispatch, language, messages]);

  return (
    <Provider container={diContainer}>
      <ErrorBoundary FallbackComponent={GlobalError}>
        <Helmet htmlAttributes={{ lang: language }} />
        <IntlProvider
          messages={messages}
          defaultLocale="en"
          locale={language}
          onError={(intlError) => {
            if (intlError.code === IntlErrorCode.MISSING_TRANSLATION) {
              return;
            }
            throw intlError;
          }}
          defaultRichTextElements={{
            // eslint-disable-next-line react/no-unstable-nested-components
            b: (chunks) => <b>{chunks}</b>,
          }}
        >
          <App />
        </IntlProvider>
      </ErrorBoundary>
    </Provider>
  );
};

export { AppWrapper };
